import { useEffect, useMemo } from "react";    
import styled from "styled-components";
import { Tag } from "../../public/Tags";
import { useNavigate } from "react-router-dom";
import { TagIF, ContentIF } from "../../types";
import { ViewIcon, ViewIconContainer, HeartIconContainer, HeartIcon, TagIcon, TagIconContainer, CalenderIconContainer, CalendarIcon, NoSearchResultIcon } from "../../icons";
import { increaseView } from "../../api/contents";
import { ONE_PAGE_MAX } from "./Home";

/* Contents.tsx는 포스트 목록(태그 및 검색 기반 필터링)을 보여주는 컴포넌트 */
interface ContentsProps {
  contents: ContentIF[],
  loading: boolean,
  selectedTags: TagIF[],
  searchedTerm: string,
  curPage: number,
  onVisibleLength: React.Dispatch<React.SetStateAction<number>>
}

// 검색어 하이라이트 함수
function highlightText(text: string, term: string){
  // 검색란 비어있을 땐 hightlight 처리 X
  if(!term) return text;

  const start = text.search(term);
  if(start === -1) return text;

  const end = start + term.length;

  console.log("Highlighting:", text, term, start, end);

  return <>{text.substring(0, start)}
        <Matched>{text.substring(start, end)}</Matched>
        {text.substring(end)}
        </>;
}

const NotFound = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 3rem;
  font-weight: bold;
  padding-top: 30vh;
  color: ${props => props.theme.NONE_TAG.FONT_COLOR};
  text-align: center;
`;

const Body = styled.div`
    background-color: ${props => props.theme.CONTENT.BACKGROUND};
    margin-bottom: 1.5rem;
    padding: 1.5rem;
    border-radius: 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    border: 1px solid ${props => props.theme.CONTENT.BORDER};
    box-shadow: 0 4px 6px -1px ${props => props.theme.CONTENT.SHADOW};

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 10px 15px -3px ${props => props.theme.CONTENT.HOVER.SHADOW};
    }
`;

const Title = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${props => props.theme.CONTENT.TITLE_COLOR};
  margin: 0;
  line-height: 1.3;
`;

const Summary = styled.p`
  color: ${props => props.theme.CONTENT.SUMMARY_COLOR};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-top: 0;
  max-width: 80%;
  /* 3줄까지만 보여주고 말줄임표 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Matched = styled.span`
  background-color: ${props => props.theme.CONTENT.MATCHED.BACKGROUND};
  font-weight: bold;
  color: ${props => props.theme.CONTENT.MATCHED.FONT_COLOR};
  padding: 0 2px;
`;

const Upper = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 0.5rem;
  border-bottom: 0.1rem solid ${props => props.theme.CONTENT.UPPER_BOTTOM_BORDER};
`;

const Bottom = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  margin-left: 0.2rem;
  max-width: 80%;
`;

const View = styled(ViewIconContainer)`
  float: right;
  color: ${props => props.theme.CONTENT.SUMMARY_COLOR};
`;

const Heart = styled(HeartIconContainer)`
  float:right;
  margin-right: 1rem;
  color: ${props => props.theme.CONTENT.SUMMARY_COLOR};
`;

export const DateTag = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.3rem;
  
  font-size: 0.8rem;
  font-weight: bold;

  margin: 0.3rem 0.4rem;
  margin-left:0;
  margin-top: auto;
  padding: 0.15rem 0.4rem;

  width: fit-content;
  height: fit-content;

  line-height: 1.5;

  color: ${props => props.theme.DATE_TAG.FONT_COLOR};
  background: linear-gradient(45deg, ${props => props.theme.DATE_TAG.BACKGROUND.FIRST} 0%, ${props => props.theme.DATE_TAG.BACKGROUND.LAST});

  border: 1px, solid, ${props => props.theme.DATE_TAG.BORDER};
  border-radius: 0.5rem;
`;

export const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  max-width: 80%;

  padding: 0.3rem 0.4rem;
  padding-left: 0;
  gap: 0.3rem;
`;

const ImgContainer = styled.div`
  width: 8rem;
  height: 8rem;
  min-width: 8rem;
  flex-shrink: 0; // 이미지 크기 고정, 부모 요소의 크기에 따라 줄어들거나 늘어나지 않도록 설정
  margin: 1rem;
`;

function Contents({contents, loading, selectedTags, searchedTerm, curPage, onVisibleLength}: ContentsProps){
    const navigate = useNavigate();

    // 선택된 Tag를 포함한 Contents
    const filteredContents = useMemo(() => {
        return selectedTags.length > 0
            ? contents.filter(content => selectedTags.every(seltag => content.tags.some(tag => tag.name === seltag.name))) 
            : contents;
    }, [contents, selectedTags]);

    // 검색어를 포함한 Contents
    const searchedContents = useMemo(() => {
        return searchedTerm
            ? filteredContents.filter(content => content.title.includes(searchedTerm) || content.summary.includes(searchedTerm))
            : filteredContents;
    }, [filteredContents, searchedTerm]);

    //  
    useEffect(() => {
      onVisibleLength(searchedContents.length);
    }, [searchedContents])

    const updateView = async (content_id:number) => {
            try {
                await increaseView(content_id);
            } catch(err) {
                alert("CONTENT 조회수 갱신 에러 발생!");
                console.error(err);
          }
      }

    if (loading) {
      return <NotFound>Loading...</NotFound>
    }

    return (<>
    {
      searchedContents.length === 0 ?
      // 검색 결과 X
      <NotFound>
        <div>
          <NoSearchResultIcon />
        </div>
        No results found
      </NotFound>
      : searchedContents.map((content, idx) => {
        if (ONE_PAGE_MAX*(curPage - 1) <= idx && idx <= ONE_PAGE_MAX*curPage - 1) {
          return (
        
        <Body key={content.id} onClick={() => { updateView(content.id); navigate(`/content/${content.id}`); }}>
          <Upper>
            <Title>{highlightText(content.title, searchedTerm)}</Title>
            <View>
              <ViewIcon />
              {content.view}
            </View>
            <Heart>
              <HeartIcon />
              {content.heart}
            </Heart>
          </Upper>
          <Bottom>
            <Info>
              <Summary>{highlightText(content.summary, searchedTerm)}</Summary>
              <DateTag>
                <CalenderIconContainer>
                  <CalendarIcon />
                </CalenderIconContainer>
                {content.createdAt.split(" ")[0]}
              </DateTag>
              <TagContainer>
                {content.tags.length > 0 ? content.tags.map(tag => (
                  <Tag key={tag.id} $selected={selectedTags.some(t => t.name === tag.name)} $isNone={false}>
                    <TagIconContainer>
                      <TagIcon />
                      {tag.name}
                    </TagIconContainer>
                    </Tag>
                )) :
                <Tag $isNone={true}>
                  <TagIconContainer>
                      <TagIcon />
                      {"None"}
                    </TagIconContainer>
                </Tag>}
              </TagContainer>
            </Info>
            {content.images.length > 0 
            ? <ImgContainer>
            <img 
              src={`${process.env.REACT_APP_API_URL}${content.images[0]}`} 
              alt="content" 
              style={{width: "100%", height: "100%", objectFit: "cover"}} 
            />
            </ImgContainer>
            : null}
          </Bottom>
        </Body>
      )}
      })
    }
    </>);
}

export default Contents;
